import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static targets = [ 
    "modal", "selectedTemplate", "templateId", "templateName", "templatePrompt", 
    "coverPreview", "formContainer", "templatesGrid", "selectorButton", "promptInput"
  ]

  connect() {
    // Close modal on escape key
    document.addEventListener("keydown", (e) => {
      if (e.key === "Escape") this.closeModal()
    })

    // Close modal on click outside
    document.addEventListener("click", (e) => {
      if (this.modalTarget.classList.contains("hidden")) return
      if (!this.modalTarget.contains(e.target) && !e.target.closest("[data-action*='prompt-template-selector#openModal']")) {
        this.closeModal()
      }
    })
  }

  disconnect() {
    document.removeEventListener("keydown", this.closeModal)
    document.removeEventListener("click", this.closeModal)
  }

  openModal() {
    if (this.hasModalTarget) {
      this.modalTarget.classList.remove("hidden")
      document.body.classList.add("overflow-hidden")
      // Load system templates by default
      Turbo.visit("/img_gen/prompt_templates?scope=system", { frame: "templates_grid" })
    }
  }

  closeModal() {
    if (this.hasModalTarget) {
      this.modalTarget.classList.add("hidden")
      document.body.classList.remove("overflow-hidden")
    }
  }

  loadSystemTemplates(event) {
    const button = event.currentTarget
    this.setActiveTab(button)
    Turbo.visit("/img_gen/prompt_templates?scope=system", { frame: "templates_grid" })
  }

  loadUserTemplates(event) {
    const button = event.currentTarget
    this.setActiveTab(button)
    Turbo.visit("/img_gen/prompt_templates?scope=user", { frame: "templates_grid" })
  }

  setActiveTab(activeButton) {
    // Remove active state from all tabs
    activeButton.closest(".flex").querySelectorAll("button").forEach(button => {
      button.classList.remove("bg-gray-800", "text-gray-200")
      button.classList.add("text-gray-400")
    })

    // Add active state to clicked tab
    activeButton.classList.remove("text-gray-400")
    activeButton.classList.add("bg-gray-800", "text-gray-200")
  }

  selectTemplate(event) {
    const templateId = event.currentTarget.dataset.templateId
    const templateName = event.currentTarget.dataset.templateName
    const templatePrompt = event.currentTarget.dataset.templatePrompt

    if (this.hasTemplateIdTarget && this.hasTemplateNameTarget && this.hasTemplatePromptTarget) {
      this.templateIdTarget.value = templateId
      this.templateNameTarget.textContent = templateName
      this.templatePromptTarget.value = templatePrompt
      
      // Show selected template and hide selector button
      this.selectedTemplateTarget.classList.remove("hidden")
      this.selectorButtonTarget.classList.add("hidden")

      // Populate and focus the prompt input
      if (this.hasPromptInputTarget) {
        this.promptInputTarget.value = templatePrompt
        this.promptInputTarget.focus()
        this.promptInputTarget.setSelectionRange(0, 0)
      }
    } 

    this.closeModal()
  }

  clearTemplate() {
    if (this.hasTemplateIdTarget && this.hasTemplateNameTarget && this.hasTemplatePromptTarget) {
      this.templateIdTarget.value = ""
      this.templateNameTarget.textContent = ""
      this.templatePromptTarget.value = ""
      
      // Hide selected template and show selector button
      this.selectedTemplateTarget.classList.add("hidden")
      this.selectorButtonTarget.classList.remove("hidden")

      // Clear the prompt input
      if (this.hasPromptInputTarget) {
        this.promptInputTarget.value = ""
        this.promptInputTarget.focus()
      }
    }
  }

  async deleteTemplate(event) {
    event.preventDefault()
    event.stopPropagation()
    
    if (!confirm("Are you sure you want to delete this template?")) return

    const templateId = event.currentTarget.dataset.templateId
    if (!templateId) return
    
    try {
      // Find the template element now, before the fetch, since event.currentTarget will be valid
      const templateElement = event.currentTarget.closest(`#template_${templateId}`)
      if (!templateElement) {
        console.warn(`Could not find template element with ID: template_${templateId}`)
      }
      
      const response = await fetch(`/img_gen/prompt_templates/${templateId}`, {
        method: "DELETE",
        headers: {
          "X-CSRF-Token": document.querySelector("[name='csrf-token']").content,
          "Accept": "text/vnd.turbo-stream.html, text/html, application/json"
        },
        credentials: "same-origin"
      })

      if (response.ok) {
        // Remove the template element from the DOM
        if (templateElement) {
          templateElement.remove()
        }
        
        // If this was the selected template, clear it
        if (this.hasTemplateIdTarget && this.templateIdTarget.value === templateId) {
          this.clearTemplate()
        }
        
        // Process the turbo stream response to handle the flash message
        const contentType = response.headers.get("Content-Type") || ""
        if (contentType.includes("text/vnd.turbo-stream.html")) {
          const html = await response.text()
          const parser = new DOMParser()
          const doc = parser.parseFromString(html, "text/html")
          
          // Find and process flash message turbo-stream
          const flashStream = doc.querySelector('turbo-stream[target="flash"]')
          if (flashStream) {
            // Create a temporary element to hold the stream
            const tempEl = document.createElement('div')
            tempEl.innerHTML = flashStream.outerHTML
            document.body.appendChild(tempEl.firstChild)
          }
        }
      }
    } catch (error) {
      console.error("Error deleting template:", error)
    }
  }

  showNewTemplateForm() {
    // Hide templates grid
    this.templatesGridTarget.classList.add("hidden")
    // Show form container
    this.formContainerTarget.classList.remove("hidden")
    // Load form
    const url = "/img_gen/prompt_templates/new"
    Turbo.visit(url, { 
      frame: "template_form",
      action: "replace"
    })
  }

  showTemplatesGrid() {
    this.formContainerTarget.classList.add("hidden")
    this.templatesGridTarget.classList.remove("hidden")
  }

  previewCover(event) {
    const file = event.target.files[0]
    if (file) {
      const reader = new FileReader()
      reader.onload = (e) => {
        const previewTarget = this.coverPreviewTarget
        previewTarget.querySelector("img").src = e.target.result
        previewTarget.classList.remove("hidden")
      }
      reader.readAsDataURL(file)
    }
  }

  stopPropagation(event) {
    event.stopPropagation()
  }
} 